import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './i18n';
import App from './App.tsx';
import { loadPortalConfig } from './lib/portal-config';
import './index.css';
import { injectCloudflareAnalytics } from './lib/cf-analytics';
import { captureDiscountCodeFromLocation } from './lib/discount-code-carry';
import { captureLandingOnce } from './lib/landing-capture';

// OCP-176 — capture referrer + landing path + ?ref= tag BEFORE React
// mounts so React Router's first navigation doesn't overwrite
// location.pathname. Stored in sessionStorage; SignupPage reads it
// at submit time.
captureLandingOnce();
// OCP-991 - a `redeem_url` may land before login or signup; the review step
// picks the code up from here. OCP-994 - and the code leaves the address bar
// right after, before React Router reads the location, so no later request of
// the page (API calls, lazy chunks) sends it as Referer. The document and its
// first scripts were already requested; the nginx log keeps no Referer.
captureDiscountCodeFromLocation();

// OCP-176 — Cloudflare Web Analytics beacon (cookie-less,
// GDPR-trivial). No-op when VITE_CF_ANALYTICS_TOKEN env-var unset
// at build time.
injectCloudflareAnalytics();

// SPA-deploy chunk-load recovery: when a customer has a tab open
// during a deploy + the SPA tries to lazy-load a route chunk, the old
// hashed-filename URL 404s (new build shipped fresh hashes). Vite
// emits `vite:preloadError` for this — we trap it and full-reload to
// get the fresh index.html + matching asset hashes. Guarded with
// sessionStorage so an asset-genuinely-missing case can't loop.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  const flag = 'orboto-portal-reload-on-preload-error';
  if (sessionStorage.getItem(flag)) return;
  sessionStorage.setItem(flag, '1');
  window.location.reload();
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('customer-portal: #root element not found in index.html');

// OCP-656 - is the language switch released? Dark default until the answer arrives.
void loadPortalConfig();

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
